"use client";

import { useState } from "react";
import { validateWholesaleInquiry, type WholesaleInquiryErrors } from "@/lib/wholesale";

const inputClass =
  "mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200";
const labelClass = "block text-xs uppercase tracking-[0.3em] text-slate-500";

export function WholesaleForm() {
  const [businessName, setBusinessName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<WholesaleInquiryErrors>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const submit = async () => {
    const input = { businessName, contactName, email, message };
    const { valid, errors: validationErrors } = validateWholesaleInquiry(input);
    setErrors(validationErrors);
    if (!valid) return;

    setStatus("submitting");
    try {
      const res = await fetch("/api/wholesale", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      setStatus(res.ok ? "success" : "error");
    } catch (err) {
      console.error("[WholesaleForm] Failed to submit", err);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-none border border-slate-200 bg-white p-6 text-center shadow-soft">
        <p className="text-lg font-semibold text-slate-900">Thank you for your interest!</p>
        <p className="mt-2 text-sm text-slate-600">
          We&apos;ve received your wholesale inquiry and will be in touch shortly.
        </p>
      </div>
    );
  }

  return (
    <form
      className="space-y-4 rounded-none border border-slate-200 bg-white p-6 text-left shadow-soft"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      noValidate
    >
      <label className={labelClass}>
        Business name
        <input
          type="text"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          className={inputClass}
          placeholder="Your store or brand"
          autoComplete="organization"
        />
        {errors.businessName && <span className="mt-1 block text-xs normal-case tracking-normal text-red-600">{errors.businessName}</span>}
      </label>
      <label className={labelClass}>
        Your name
        <input
          type="text"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          className={inputClass}
          placeholder="First and last name"
          autoComplete="name"
        />
        {errors.contactName && <span className="mt-1 block text-xs normal-case tracking-normal text-red-600">{errors.contactName}</span>}
      </label>
      <label className={labelClass}>
        Email
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
          placeholder="you@example.com"
          autoComplete="email"
        />
        {errors.email && <span className="mt-1 block text-xs normal-case tracking-normal text-red-600">{errors.email}</span>}
      </label>
      <label className={labelClass}>
        Message <span className="text-slate-400">(optional)</span>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="mt-2 min-h-[120px] w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
          placeholder="Tell us about your business and what you'd like to stock..."
        />
      </label>
      {status === "error" && (
        <p className="text-sm text-red-600">Something went wrong. Please try again or email us directly.</p>
      )}
      <button
        type="submit"
        disabled={status === "submitting"}
        className="w-full rounded-none bg-[var(--primary)] px-6 py-3 text-sm font-semibold uppercase tracking-[0.4em] text-white shadow-soft transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {status === "submitting" ? "Sending…" : "Submit inquiry"}
      </button>
    </form>
  );
}
